import React, { useEffect, useRef, useState } from "react";
import { FaRegClock, FaExclamationTriangle, FaTimesCircle } from "react-icons/fa";
import CountdownBlocks, { countdownLabels } from "./CountdownBlocks";
import "./ActivationCountdown.css";

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["activationCountdown"]?.[key] || fallback;

const DAY_MS = 86400000;
const HOUR_MS = 3600000;
const MINUTE_MS = 60000;

// Single source of truth for "how much time is left before an activation
// link expires" -- reused by addAdminFamily's success card, CardUser's User
// Activity modal, and the /user_Shoosing/:token page (mission section 5:
// never three separate timer implementations). Always driven by
// User.ativator[].experyDate (via the `expiresAt` prop) -- never
// expireAtUser, userActiveRole.endDate, or createdAt.
export const calculateRemainingTime = (expiresAt) => {
  if (!expiresAt) return null;
  const target = new Date(expiresAt).getTime();
  if (!Number.isFinite(target)) return null;

  const difference = target - Date.now();
  if (difference <= 0) {
    return { expired: true, totalMilliseconds: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  return {
    expired: false,
    totalMilliseconds: difference,
    days: Math.floor(difference / DAY_MS),
    hours: Math.floor((difference % DAY_MS) / HOUR_MS),
    minutes: Math.floor((difference % HOUR_MS) / MINUTE_MS),
    seconds: Math.floor((difference % MINUTE_MS) / 1000),
  };
};

const pad2 = (n) => String(n).padStart(2, "0");

// variant="blocks": the large four-block layout (CountdownBlocks) used on the
// /user_Shoosing/:token page; the default and `compact` layouts are unchanged.
const ActivationCountdown = ({ infoSource, expiresAt, onExpire, showLabel = true, compact = false, variant = "inline", className = "" }) => {
  __setElakayInfoSource(infoSource);
  const [remainingTime, setRemainingTime] = useState(() => calculateRemainingTime(expiresAt));
  const hasFiredExpireRef = useRef(false);

  useEffect(() => {
    hasFiredExpireRef.current = false;

    const updateRemainingTime = () => {
      setRemainingTime(calculateRemainingTime(expiresAt));
    };

    updateRemainingTime();
    const intervalId = window.setInterval(updateRemainingTime, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [expiresAt]);

  useEffect(() => {
    if (remainingTime?.expired && !hasFiredExpireRef.current) {
      hasFiredExpireRef.current = true;
      onExpire?.();
    }
  }, [remainingTime, onExpire]);

  if (!expiresAt || !remainingTime) return null;

  if (remainingTime.expired) {
    return (
      <div className={`activation-countdown activation-countdown--expired ${className}`} role="status">
        <FaTimesCircle />
        <span>{__elakayText("expiredMessage", "This activation link has expired.")}</span>
      </div>
    );
  }

  // Mission: never rely on color alone -- always pair with text/icon.
  const isCritical = remainingTime.totalMilliseconds <= HOUR_MS;
  const isWarning = !isCritical && remainingTime.totalMilliseconds <= DAY_MS;
  const severity = isCritical ? "critical" : isWarning ? "warning" : "normal";
  const Icon = isCritical ? FaExclamationTriangle : FaRegClock;

  if (variant === "blocks") {
    return (
      <CountdownBlocks
        remaining={remainingTime}
        labels={countdownLabels(infoSource)}
        severity={severity}
        className={className}
      />
    );
  }

  if (compact) {
    return (
      <div className={`activation-countdown activation-countdown--compact activation-countdown--${severity} ${className}`} role="status">
        <Icon />
        <span>
          {__elakayText("expiresIn", "Expires in")} {remainingTime.days}{__elakayText("daysAbbrev", "d")} {pad2(remainingTime.hours)}:{pad2(remainingTime.minutes)}:{pad2(remainingTime.seconds)}
        </span>
      </div>
    );
  }

  return (
    <div className={`activation-countdown activation-countdown--${severity} ${className}`} role="status">
      <Icon className="activation-countdown-icon" />
      <div>
        {showLabel && <span className="activation-countdown-label">{__elakayText("remainingTime", "Time remaining")}</span>}
        <span className="activation-countdown-value">
          {remainingTime.days} {__elakayText("days", "days")} {pad2(remainingTime.hours)} {__elakayText("hoursAbbrev", "h")} {pad2(remainingTime.minutes)} {__elakayText("minutesAbbrev", "min")} {pad2(remainingTime.seconds)} {__elakayText("secondsAbbrev", "s")}
        </span>
      </div>
    </div>
  );
};

export default ActivationCountdown;
